// Shared "pending auth destination" primitive (RFC-0015 Phase 0, Task 0.1).
//
// A single sessionStorage entry, written once per login attempt and consumed
// once, that lets browser-only navigation state survive the login round trip
// Kratos does not fully control. It backs two of RFC-0015's gaps:
//   - Gap A: restore the URL fragment (`location.hash`) after login.
//   - Gap D: recover the original deep link across Kratos' internal
//     login -> registration flow switch (which is not guaranteed to carry
//     `return_to`).
//
// Security posture (RFC-0015 §Security):
//   - The stored `returnTo` is NEVER trusted as-is. It is re-validated through
//     the existing `sanitizeReturnTo` (auth-api.ts) on every read; a value that
//     fails sanitization is discarded and `null` is returned.
//   - A `createdAt` timestamp bounds the entry to a 15-minute TTL, so an
//     abandoned login attempt in one tab cannot leak into a later, unrelated
//     one.
//   - `consume` is one-shot: reading removes the entry regardless of validity.
//
// DOM-guarded so it stays unit-testable under jsdom and never throws in an
// SSR / storage-disabled context (private mode, quota, blocked storage).

import { sanitizeReturnTo } from '@/lib/auth-api';

/** sessionStorage key holding the single pending-auth-destination entry. */
const STORAGE_KEY = 'portal.pendingAuthDestination';

/**
 * Time-to-live for a stashed entry (RFC-0015 Resolved Question #2): 15 minutes,
 * comfortably inside `login.lifespan: 10m` plus OIDC round-trip margin. Not
 * load-bearing — a stale entry is simply discarded and the user lands on the
 * resolved path.
 */
const TTL_MS = 15 * 60 * 1000;

/** The shape persisted to sessionStorage. Internal — never returned raw. */
interface StoredPendingAuthDestination {
  /** Untrusted at rest — re-sanitized via `sanitizeReturnTo` on read. */
  returnTo: string;
  /** `location.hash` captured before the redirect; empty when there was none. */
  hash: string;
  /** Write timestamp (epoch ms); bounds the entry to `TTL_MS`. */
  createdAt: number;
}

/** The sanitized, TTL-valid entry returned to callers by `consume`. */
export interface PendingAuthDestination {
  /** Sanitized relative path + search (never the raw stored value). */
  returnTo: string;
  /** The captured `location.hash` (may be empty). */
  hash: string;
}

/**
 * Return sessionStorage if it is present and usable, else `null`. Accessing the
 * global can throw in some environments (blocked storage), so it is guarded —
 * an SSR / storage-disabled context yields `null` and every op becomes a no-op.
 */
function getSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Persist the pending auth destination. Called from the redirect-to-login path
 * (`auth.tsx`) and from `/login` immediately before handing off to Kratos.
 *
 * Wrapped in try/catch so a storage failure (quota, private mode) never throws
 * into the redirect path — the deep link is a best-effort convenience, never a
 * correctness dependency.
 */
export function stashPendingAuthDestination(returnTo: string, hash: string): void {
  const store = getSessionStorage();
  if (!store) return;

  const entry: StoredPendingAuthDestination = {
    returnTo,
    hash,
    createdAt: Date.now(),
  };

  try {
    store.setItem(STORAGE_KEY, JSON.stringify(entry));
    // RFC-0015 §Logging: INFO on stash, field `has_hash`.
    console.info('[auth] pending destination stashed', { has_hash: hash.length > 0 });
  } catch {
    // Storage full / disabled — never throw into the redirect path.
  }
}

/**
 * Read AND clear the pending auth destination in one call (one-shot). Returns
 * `null` — and clears any entry — when:
 *   - nothing was stashed,
 *   - the entry is malformed,
 *   - the TTL has elapsed (`Date.now() - createdAt > TTL_MS`), or
 *   - `sanitizeReturnTo` rejects the stored `returnTo`.
 *
 * On success returns the SANITIZED `returnTo` (never the raw stored value) plus
 * the captured hash.
 */
export function consumePendingAuthDestination(): PendingAuthDestination | null {
  const store = getSessionStorage();
  if (!store) return null;

  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  // One-shot: remove the entry regardless of whether it survives validation.
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — a failed clear must not block the read path.
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (
    parsed === null ||
    typeof parsed !== 'object' ||
    typeof (parsed as StoredPendingAuthDestination).returnTo !== 'string' ||
    typeof (parsed as StoredPendingAuthDestination).createdAt !== 'number'
  ) {
    return null;
  }
  const entry = parsed as StoredPendingAuthDestination;

  const expired = Date.now() - entry.createdAt > TTL_MS;
  if (expired) {
    // RFC-0015 §Logging: INFO on consume, field `expired`. (`matched_path` is
    // logged at the restore site in Phase 1, which knows the current path.)
    console.info('[auth] pending destination consumed', { expired: true });
    return null;
  }

  // Never trust the stored path — re-validate through the existing sanitizer.
  const safe = sanitizeReturnTo(entry.returnTo);
  if (!safe) return null;

  const hash = typeof entry.hash === 'string' ? entry.hash : '';
  console.info('[auth] pending destination consumed', { expired: false, has_hash: hash.length > 0 });
  return { returnTo: safe, hash };
}

/**
 * Pure guard for Gap A's cross-path hash-leakage defense: a restored hash must
 * only be applied when the current `pathname + search` matches the destination
 * the hash was captured for. Exact comparison — a stale hash for a different
 * page is dropped.
 */
export function matchesReturnTo(currentPathSearch: string, returnTo: string): boolean {
  return currentPathSearch === returnTo;
}
